/**
 * EmployeeTypePicker - Wrapper around generic EntityPicker
 * 
 * Provides employee type-specific configuration and maintains backward compatibility
 * with any code that imports EmployeeTypePickerField or EmployeeTypePickerModal
 */

import React from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';
import { EntityPickerSelect } from '../EntityPicker/EntityPickerSelect';

// ── EmployeeType-specific configuration ──────────────────────────────────────
export const EMPLOYEETYPE_CONFIG = {
	apiEndpoint: "/api/EmployeeType/active",
	entityName: "Employee Type",
	idField: "id",
	displayFields: {
		primary: "description"
	},
	tableHeaders: ["Description"],
	tableAccessors: ["description"],
	searchFields: [
		{ label: "Description", field: "description", index: 0 },
	],
	placeholder: "--Select type--"
};

// ── EmployeeTypePickerField wrapper ──────────────────────────────────────────
/**
 * EmployeeTypePickerField
 * 
 * Usage:
 * <EmployeeTypePickerField
 *   label="Employee Type"
 *   employeeTypeId={form.employeeTypeId}
 *   displayValue={form.employeeTypeDescription}
 *   onSelect={(type) => {
 *     set("employeeTypeId", type.id);
 *     set("employeeTypeDescription", type.description);
 *   }}
 *   onClear={() => {
 *     set("employeeTypeId", "");
 *     set("employeeTypeDescription", "");
 *   }}
 *   disabled={!editable}
 * />
 */
export function EmployeeTypePickerField({
	employeeTypeId,     // type-specific prop name
	displayValue = "",
	onSelect,
	onClear,
	...rest             // label, isRequired, error, disabled, etc.
}) {
	return (
		<EntityPickerField
			{...rest}
			config={EMPLOYEETYPE_CONFIG}
			entityId={employeeTypeId}
			displayValue={displayValue}
			onSelect={onSelect}
			onClear={onClear}
		/>
	);
}

// ── EmployeeTypePickerSelect wrapper ─────────────────────────────────────────
// ADDED: Select version for dropdown usage
export function EmployeeTypePickerSelect({ value, onChange, ...rest }) {
  return (
    <EntityPickerSelect
      {...rest}
      config={EMPLOYEETYPE_CONFIG}
      value={value}
      onChange={onChange}
    />
  );
}

// ── EmployeeTypePickerModal wrapper ──────────────────────────────────────────
/**
 * EmployeeTypePickerModal
 * 
 * Usage (standalone):
 * <EmployeeTypePickerModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   setData={(type) => {
 *     set("employeeTypeId", type.id);
 *     set("employeeTypeDescription", type.description);
 *   }}
 * />
 */
export function EmployeeTypePickerModal({ isOpen, onClose, setData }) {
	return (
		<EntityPickerModal
			isOpen={isOpen}
			onClose={onClose}
			setData={setData}
			config={EMPLOYEETYPE_CONFIG}
		/>
	);
}

// ── Exports (backward compatibility) ─────────────────────────────────────────
export { EmployeeTypePickerField as default };